import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import * as SkeletonUtils from 'three/examples/jsm/utils/SkeletonUtils.js';
import { CharacterAnimationFsm } from './character-animation-fsm.js';

const BASE = import.meta.env.BASE_URL;

export type PremiumCharacterRole = 'waiter' | 'client';

export interface PremiumCharacterInstance {
  role: PremiumCharacterRole;
  root: THREE.Group;
  fsm: CharacterAnimationFsm;
}

interface LoadedTemplate {
  scene: THREE.Group;
  clips: THREE.AnimationClip[];
}

const cache = new Map<string, LoadedTemplate>();

async function loadTemplate(url: string): Promise<LoadedTemplate> {
  const hit = cache.get(url);
  if (hit) return hit;
  const loader = new GLTFLoader();
  const gltf = await loader.loadAsync(url);
  const tpl: LoadedTemplate = { scene: gltf.scene as THREE.Group, clips: gltf.animations };
  cache.set(url, tpl);
  return tpl;
}

function dressWaiter(root: THREE.Object3D): void {
  const apron = new THREE.Color(0xf8f8f8);
  const vest = new THREE.Color(0x2f4f6f);
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || !mesh.material) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const mat of mats) {
      if (!(mat instanceof THREE.MeshStandardMaterial)) continue;
      const n = (mat.name || mesh.name || '').toLowerCase();
      if (n.includes('pants') || n.includes('leg')) mat.color.lerp(vest, 0.35);
      else if (n.includes('shirt') || n.includes('torso') || n.includes('body')) mat.color.lerp(vest, 0.55);
      else mat.color.lerp(apron, 0.15);
      mat.roughness = 0.62;
    }
  });
  const badge = new THREE.Mesh(
    new THREE.BoxGeometry(0.12, 0.08, 0.02),
    new THREE.MeshStandardMaterial({ color: 0xc9a227, roughness: 0.4, metalness: 0.35 }),
  );
  badge.position.set(0.08, 1.35, 0.14);
  root.add(badge);
}

function tintClient(root: THREE.Object3D, hex: number): void {
  const c = new THREE.Color(hex);
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || !mesh.material) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const mat of mats) {
      if (mat instanceof THREE.MeshStandardMaterial) mat.color.lerp(c, 0.22);
    }
  });
}

export async function createPremiumCharacter(
  role: PremiumCharacterRole,
): Promise<PremiumCharacterInstance> {
  const url =
    role === 'waiter'
      ? `${BASE}models/characters/waiter-male.glb`
      : `${BASE}models/characters/client-female.glb`;
  const tpl = await loadTemplate(url);
  const root = SkeletonUtils.clone(tpl.scene) as THREE.Group;
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh) {
      m.castShadow = true;
      m.receiveShadow = true;
    }
  });
  if (role === 'waiter') dressWaiter(root);
  else tintClient(root, 0xb5651d);
  const fsm = new CharacterAnimationFsm(root, tpl.clips);
  return { role, root, fsm };
}
